import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  type ElementDropTargetEventBasePayload,
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { createPortal } from 'react-dom';
import invariant from 'tiny-invariant';
import { Box, Card } from 'ui';

import { DroppedElementCardContent } from './DroppedElementCardContent';
import classes from './DroppedElementItem.module.css';
import { FocusableCard } from './FocusableCard';
import { draggingState, idleState } from './constants';
import type {
  BottomSectionValues,
  DragState,
  DroppedElement,
  Edge,
  FocusableCardHandle,
} from './types';
import { getDroppedItemData, isDroppedItemData } from './utils';

// Component for rendering a single dropped element that can be reordered
export const DroppedElementItem = forwardRef<
  FocusableCardHandle,
  {
    element: DroppedElement;
    index: number;
    instanceId: symbol;
    onDropOriginalElement: (
      draggedData: { id: string; label: string; type: string; component: string },
      targetIndex: number
    ) => void;
    onDelete: (elementId: string) => void;
    onDuplicate: (element: DroppedElement, targetIndex: number) => void;
    onBottomSectionValueChange?: (elementId: string, values: BottomSectionValues) => void;
    onValueChange?: (elementId: string, value: string) => void;
    renderComponent: (
      element: DroppedElement,
      focused: boolean,
      onValueChange?: (elementId: string, value: string) => void
    ) => React.ReactNode;
  }
>(
  (
    {
      element,
      index,
      instanceId,
      onDropOriginalElement,
      onBottomSectionValueChange,
      onDelete,
      onDuplicate,
      onValueChange,
      renderComponent,
    },
    forwardedRef
  ) => {
    const ref = useRef<HTMLDivElement>(null);
    const focusableCardRef = useRef<FocusableCardHandle>(null);
    const dragHandleRef = useRef<HTMLDivElement>(null);
    const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
    const [focused, setFocused] = useState(false);
    const [dragState, setDragState] = useState<DragState>(idleState);

    // Handle focus changes from DroppedElementCardContent
    const handleFocusChange = useCallback((isFocused: boolean) => {
      setFocused(isFocused);
    }, []);

    // Forward the ref to the focusable card
    useImperativeHandle(forwardedRef, () => ({
      focus: () => {
        focusableCardRef.current?.focus();
      },
    }));

    // Show me where I CAN drop the element
    const onChange = useCallback(
      ({ source, self }: ElementDropTargetEventBasePayload) => {
        // Self-Drag Prevention (Lines 81-85)
        const isSource = source.element === ref.current;
        if (isSource) {
          setClosestEdge(null);
          return;
        }

        const closestEdge = extractClosestEdge(self.data) as Edge | null;

        // Detects when dragging new elements from the toolbar
        if (!isDroppedItemData(source.data)) {
          // Handle original draggable elements
          const draggedData = source.data as {
            id: string;
            label: string;
            type: string;
            component: string;
          };
          if (draggedData.type === 'draggable-element') {
            setClosestEdge(closestEdge);
          }
          return;
        }

        const sourceIndex = source.data.index;

        invariant(
          typeof sourceIndex === 'number',
          'DroppedElementItem: Source index is not a number'
        );

        const isItemBeforeSource = index === sourceIndex - 1;
        const isItemAfterSource = index === sourceIndex + 1;

        // Hide Redundant Indicators
        const isDropIndicatorHidden =
          (isItemBeforeSource && closestEdge === 'bottom') ||
          (isItemAfterSource && closestEdge === 'top');

        if (isDropIndicatorHidden) {
          setClosestEdge(null);
          return;
        }

        setClosestEdge(closestEdge);
      },
      [index]
    );

    // I dropped element, now DO something with it
    const handleDrop = useCallback(
      ({ source, self }: ElementDropTargetEventBasePayload) => {
        // Determines if this is a new element from the toolbar, or an existing element being reordered
        if (!isDroppedItemData(source.data)) {
          // Handle original draggable elements (from the toolbar)
          const draggedData = source.data as {
            id: string;
            label: string;
            type: string;
            component: string;
          };
          if (draggedData.type === 'draggable-element') {
            const closestEdge = extractClosestEdge(self.data) as Edge | null;
            let targetIndex = index;
            if (closestEdge === 'bottom') {
              targetIndex = index + 1;
            }
            onDropOriginalElement(draggedData, targetIndex);
          }
          setClosestEdge(null);
          return;
        }

        // Reordering is handled by the global monitor (DropTarget)
        // Just clean up the visual indicators
        setClosestEdge(null);
      },
      [index, onDropOriginalElement]
    );

    useEffect(() => {
      const htmlElement = ref.current;
      const dragHandle = dragHandleRef.current;
      invariant(htmlElement, 'DroppedElementItem: Element not found');

      const data = getDroppedItemData({ item: element, index, instanceId });

      // Always set up both draggable and drop target functionality
      // When unfocused, the drag handle is hidden but the entire card is still draggable
      const dragElement = dragHandle || htmlElement; // Use drag handle if available, otherwise the whole element

      return combine(
        draggable({
          element: dragElement, // Use drag handle when focused, whole element when unfocused
          getInitialData: () => data,
          onGenerateDragPreview({ nativeSetDragImage }) {
            setCustomNativeDragPreview({
              nativeSetDragImage,
              render({ container }) {
                setDragState({ type: 'preview', container });

                return () => setDragState(draggingState);
              },
            });
          },
          onDragStart() {
            setDragState(draggingState);
          },
          onDrop() {
            setDragState(idleState);
          },
        }),
        dropTargetForElements({
          element: htmlElement,
          canDrop({ source }) {
            // Accept both dropped items and original draggable elements
            return (
              (isDroppedItemData(source.data) && source.data.instanceId === instanceId) ||
              source.data.type === 'draggable-element'
            );
          },
          getData({ input }) {
            return attachClosestEdge(data, {
              element: htmlElement,
              input,
              allowedEdges: ['top', 'bottom'],
            });
          },
          onDragEnter: onChange,
          onDrag: onChange,
          onDragLeave() {
            setClosestEdge(null);
          },
          onDrop: handleDrop,
        })
      );
    }, [onChange, handleDrop, instanceId, element, index, focused]);

    const handleDelete = useCallback(() => {
      onDelete(element.id);
    }, [element.id, onDelete]);

    const handleDuplicate = useCallback(() => {
      onDuplicate(element, index + 1);
    }, [element, index, onDuplicate]);

    const draggableElementRect = ref.current?.getBoundingClientRect();

    return (
      <Box ref={ref} className={classes.container}>
        {/* Always show drop indicators during drag operations */}
        {closestEdge === 'top' && (
          <Box className={classes.dropIndicatorTop}>
            <DropIndicator edge="top" gap="0" />
          </Box>
        )}
        <FocusableCard ref={focusableCardRef}>
          <DroppedElementCardContent
            element={element}
            dragHandleRef={dragHandleRef}
            handleDuplicate={handleDuplicate}
            handleDelete={handleDelete}
            onFocusChange={handleFocusChange}
            onValueChange={onValueChange}
            focused={focused}
            onBottomSectionValueChange={onBottomSectionValueChange}
            renderComponent={renderComponent}
          />
        </FocusableCard>
        {/* Always show drop indicators during drag operations */}
        {closestEdge === 'bottom' && (
          <Box className={classes.dropIndicatorBottom}>
            <DropIndicator edge="bottom" gap="2px" />
          </Box>
        )}

        {/* Custom drag preview portal */}
        {dragState.type === 'preview' &&
          createPortal(
            <Card
              radius="sm"
              w={draggableElementRect?.width ? `${draggableElementRect.width}px` : '300px'}
              padding="none"
              shadow="sm"
              className={classes.dragPreviewCard}
            >
              <DroppedElementCardContent
                element={element}
                dragHandleRef={dragHandleRef}
                handleDuplicate={handleDuplicate}
                handleDelete={handleDelete}
                onFocusChange={handleFocusChange}
                focused={true} // Always show as focused in preview
                renderComponent={renderComponent}
              />
            </Card>,
            dragState.container
          )}
      </Box>
    );
  }
);

DroppedElementItem.displayName = 'DroppedElementItem';
